import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Home as HomeIcon } from "lucide-react";
import { NetworkMesh } from "@/components/NetworkMesh";

const LINKS = [
  ["/services", "All services"],
  ["/services/vapt", "VAPT & penetration testing"],
  ["/services/managed-security", "Managed SOC"],
  ["/services/open-source-soc", "Open Source SOC Model"],
  ["/insights", "Insights"],
  ["/resources", "Resources"],
  ["/contact", "Contact"],
];

export default function NotFound() {
  return (
    <div
      className="relative flex min-h-[88svh] items-center overflow-hidden lg:min-h-[92vh]"
      data-testid="not-found-page"
    >
      <div className="absolute inset-0 -z-10 opacity-25" aria-hidden>
        <NetworkMesh labels={false} />
      </div>
      <div className="glow-orb -left-32 top-1/3 h-[420px] w-[420px] bg-[#8E9AEA]/10" aria-hidden />

      <div className="mx-auto w-full max-w-3xl px-5 pb-16 pt-28 sm:px-8 sm:pt-32 lg:px-16">
        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="font-display text-sm font-bold uppercase tracking-[0.3em] text-[#8E9AEA]"
        >
          Error 404
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.08, ease: [0.22, 1, 0.36, 1] }}
          className="mt-4 font-display text-[1.8rem] font-extrabold leading-[1.15] tracking-tight text-white sm:text-[2.4rem] lg:text-[2.9rem]"
          data-testid="not-found-title"
        >
          This page doesn&apos;t exist.
          <span className="mt-1.5 block text-[#8E9AEA]">Everything else still does.</span>
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.18 }}
          className="mt-5 max-w-xl text-[15px] leading-relaxed text-[#DDE3F2] sm:text-base"
        >
          The link may be outdated or mistyped. Pick a destination below, or tell us what you were looking for and a
          Lanvaus engineer will point you to it.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.26 }}
          className="mt-8 cta-row"
        >
          <Link to="/" className="btn-primary" data-testid="not-found-home-cta">
            <HomeIcon className="h-4 w-4" /> Back to home
          </Link>
          <Link to="/contact" className="btn-secondary" data-testid="not-found-contact-cta">
            Contact us
          </Link>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.35 }}
          className="mt-10 border-t border-white/10 pt-6"
        >
          <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#A6B0C9]">Popular pages</p>
          <div className="mt-4 flex flex-wrap gap-2.5">
            {LINKS.map(([to, label]) => (
              <Link
                key={to}
                to={to}
                className="inline-flex items-center gap-1.5 rounded-lg border border-white/12 bg-white/[0.03] px-3 py-2 text-xs font-medium text-[#DDE3F2] transition-colors duration-200 hover:border-[#8E9AEA]/50 hover:text-white"
                data-testid={`not-found-link-${to.replace(/\//g, "-").replace(/^-/, "") || "home"}`}
              >
                {label}
                <ArrowRight className="h-3 w-3 text-[#8E9AEA]" />
              </Link>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
